import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn as UseReactHookFormReturn, useForm as useReactHookForm } from 'react-hook-form';

import { ObjectWithId } from '@shared/utils/types';

type UseFormOptions<T extends object, Source extends object> = {
  source?: {
    id: number;
    provider: (identifier: ObjectWithId) => Promise<Source>;
    onLoad?: (data: Source) => void;
    transform?: (data: Source) => Source;
  };
  defaults?: Partial<T>;
};

type UseFormReturn<T extends object> = {
  form: UseReactHookFormReturn<T> & {
    loaded: boolean;
  };
  fields: Record<keyof T, keyof T>;
};

export function useForm<T extends object, Source extends object = {}>({
  source,
  defaults,
}: UseFormOptions<T, Source> = {}): UseFormReturn<T> {
  const [loaded, setLoaded] = useState(!source);

  const defaultValues = useMemo(() => defaults, [JSON.stringify(defaults)]);

  const form = useReactHookForm<T>({
    // @ts-expect-error
    defaultValues,
  });

  useEffect(() => {
    if (!source) {
      return;
    }

    source.provider({ id: source.id }).then((sourceData) => {
      setLoaded(true);

      const data = source.transform?.(sourceData) ?? sourceData;

      // @ts-expect-error
      form.reset(data, { keepDefaultValues: false });

      source.onLoad?.(data);
    });
  }, [source?.id]);

  const fields = useMemo(
    () =>
      new Proxy(
        {},
        {
          get(_target, prop) {
            return prop;
          },
        }
      ),
    []
  ) as Record<keyof T, keyof T>;

  // @ts-expect-error
  form.loaded = loaded;

  // @ts-expect-error
  return { form, fields };
}
